
import React from 'react';
import { motion } from 'framer-motion';
import { socialLinks } from '../constants';
import { useSEO, seoConfigs } from '../hooks/useSEO';

export const ContactPage: React.FC = () => {
  useSEO(seoConfigs.contact);
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      }
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Background Effects */}
      <div className="fixed inset-0 z-0">
        <div className="absolute -top-32 left-1/2 w-[36rem] h-[36rem] -translate-x-1/2 rounded-full blur-[140px] opacity-60 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.18) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] translate-x-1/4 translate-y-1/4 rounded-full blur-[140px] opacity-50 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(129,140,248,0.15) 0%, transparent 70%)' }} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(15,23,42,0.35),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 mix-blend-soft-light opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
      </div>

      {/* Content */}
      <motion.div
        className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-16"
        variants={itemVariants}
      >
        <motion.div
          className="text-center mb-12"
          variants={itemVariants}
        >
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-sky-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
            Connect
          </h1>
          
          <motion.div
            className="max-w-3xl mx-auto text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed"
            variants={itemVariants}
          >
            <p className="mb-6">
              Let's create something extraordinary together. 
              Whether it's film sound design, music composition, or creative collaboration, 
              I'm always excited to connect with fellow creators and storytellers.
            </p>
            <p>
              Find me across social platforms or reach out directly to discuss your next project.
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          className="bg-black/30 backdrop-blur-xl rounded-3xl border border-white/10 p-8 w-full max-w-4xl"
          variants={itemVariants}
          whileHover={{ 
            scale: 1.02,
            boxShadow: "0 20px 40px rgba(56, 189, 248, 0.2)"
          }}
          transition={{ duration: 0.3 }}
        >
          <motion.h2 
            className="text-3xl sm:text-4xl font-semibold text-sky-400 mb-8 text-center"
            variants={itemVariants}
          >
            Connect on Social Media
          </motion.h2>
          
          <motion.div 
            className="flex flex-wrap justify-center gap-4"
            variants={itemVariants}
          >
            {socialLinks.map((link, index) => (
              <motion.a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
                aria-label={link.name}
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.1,
                  y: -5
                }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <div className="absolute inset-0 rounded-full blur-xl bg-sky-500/20 group-hover:bg-sky-400/40 transition-all duration-300" />
                <div className="relative bg-black/40 backdrop-blur-sm rounded-full p-4 border border-white/10 group-hover:border-sky-400/50 transition-all duration-300">
                  {link.type === 'image' ? (
                    <img 
                      src={link.icon} 
                      alt={link.name} 
                      className="h-8 w-8 object-contain filter brightness-75 group-hover:brightness-100 transition-all duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <svg
                      className="h-8 w-8 text-gray-400 group-hover:text-sky-400 transition-colors duration-300"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d={link.icon} />
                    </svg>
                  )}
                </div>
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                  {link.name}
                </div>
              </motion.a>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          className="mt-16 text-center text-sm text-gray-400"
          variants={itemVariants}
        >
          <p>Available for collaborations worldwide</p>
          <p className="mt-2 text-sky-200/60">Let's tell stories through sound</p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};
